import { createContext, useContext, useEffect, useMemo } from "react";

type Theme = "light";

interface ThemeContextType {
  theme: Theme;
  /** Conservé pour compatibilité : ne fait rien (thème clair uniquement). */
  toggleTheme: () => void;
  setTheme: (_t: "light" | "dark") => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = "dashboard-theme";
const LEGACY_KEY = "fastsell-theme";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    document.body.setAttribute("data-dashboard-theme", "light");
    localStorage.setItem(STORAGE_KEY, "light");
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) localStorage.removeItem(LEGACY_KEY);
    return () => {
      document.body.removeAttribute("data-dashboard-theme");
    };
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      theme: "light",
      toggleTheme: () => {},
      setTheme: () => {},
    }),
    [],
  );

  return (
    <ThemeContext.Provider value={value}>
      <div
        id="dashboard-app-root"
        data-dashboard-theme="light"
        className="min-h-screen min-h-dvh w-full bg-background text-foreground antialiased"
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
